import { useParams, Navigate, Link } from "react-router-dom";
import { exams } from "@/data/content";
import {useSiteSettingsContext} from "@/hooks/useSiteSettingsContext";
import PageShell from "./PageShell";
import SiteCTA from "./SiteCTA";
import RelatedLinks from "./RelatedLinks";
import Reveal from "@/components/base/Reveal";
import usePageMeta from "@/hooks/usePageMeta";

// Her sınav grubuna özgü görsel vurgu — sınav sayfaları birbirinden ayrışır.
interface Tone {
  badge: string;
  soft: string;
  text: string;
  border: string;
  dot: string;
}

const toneMap: Record<string, Tone> = {
  ortaokul: {
    badge: "bg-accent-500 text-background-50",
    soft: "bg-accent-100/80",
    text: "text-accent-700",
    border: "border-accent-300",
    dot: "bg-accent-500",
  },
  universite: {
    badge: "bg-primary-500 text-background-50",
    soft: "bg-primary-100/80",
    text: "text-primary-700",
    border: "border-primary-300",
    dot: "bg-primary-500",
  },
  kamu: {
    badge: "bg-secondary-500 text-background-50",
    soft: "bg-secondary-100/80",
    text: "text-secondary-700",
    border: "border-secondary-300",
    dot: "bg-secondary-500",
  },
  dil: {
    badge: "bg-foreground-950 text-background-50",
    soft: "bg-foreground-100",
    text: "text-foreground-900",
    border: "border-foreground-300",
    dot: "bg-foreground-950",
  },
};

function HeroStat({ value, label }: { value: string; label: string }) {
  return (
    <div className="flex flex-col gap-0.5">
      <span className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 tracking-tight">
        {value}
      </span>
      <span className="text-xs md:text-sm text-foreground-500 leading-snug">{label}</span>
    </div>
  );
}

export default function ExamDetailPage({ slugOverride }: { slugOverride?: string }) {
  const { slug } = useParams();
  const { settings } = useSiteSettingsContext();
  const name = settings.siteName || "DersDinle";
  const resolvedSlug = slugOverride ?? slug;
  const exam = exams.find((e) => e.slug === resolvedSlug);

  usePageMeta(
    exam ? `${exam.shortName} Hazırlık | ${name}` : `Sınavlar | ${name}`,
    exam
      ? `${exam.shortName} (${exam.fullName}) hazırlık: konu dağılımı, dersler, örnek çalışma rotası ve ${name} ile planlı çalışma.`
      : undefined,
  );

  if (!exam) return <Navigate to="/sinavlar" replace />;

  const tone = toneMap[exam.group] ?? toneMap["universite"];

  const crumbs = [
    { label: "Ana Sayfa", href: "/" },
    { label: "Sınavlar", href: "/sinavlar" },
    { label: exam.shortName },
  ];

  const related = exams
    .filter((e) => e.slug !== exam.slug)
    .slice(0, 4)
    .map((e) => ({ label: e.shortName, href: `/sinavlar/${e.slug}` }));
  related.push({ label: "Tüm Sınavlar", href: "/sinavlar" });

  return (
    <PageShell crumbs={crumbs}>
      {/* Hero */}
      <section className="bg-background-100 px-4 md:px-6 lg:px-10 py-14 md:py-20 border-b border-background-200">
        <div className="max-w-6xl mx-auto">
          <Reveal>
            <span
              className={`inline-flex items-center gap-2 rounded-full px-3.5 py-1.5 text-xs font-semibold whitespace-nowrap ${tone.badge}`}
            >
              <i className="ri-sparkling-2-line" aria-hidden="true"></i>
              {exam.groupLabel}
            </span>
            <h1 className="mt-5 text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight text-foreground-950 leading-tight">
              {exam.shortName}
              <span className="block text-xl md:text-2xl font-semibold text-foreground-500 mt-1">
                {exam.fullName}
              </span>
            </h1>
            <p className="mt-5 text-base md:text-lg text-foreground-600 leading-relaxed max-w-2xl">
              {exam.description}
            </p>
          </Reveal>
        </div>
      </section>

      {/* Sınav formatı — istatistik bandı */}
      <section className="bg-background-50 px-4 md:px-6 lg:px-10 py-8 md:py-10 border-b border-background-200">
        <div className="max-w-6xl mx-auto">
          <span className="inline-flex items-center gap-2.5 text-xs font-semibold uppercase tracking-[0.14em] text-foreground-500">
            <span className={`w-5 h-px ${tone.dot}`} aria-hidden="true"></span>
            Sınav formatı
          </span>
          <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-6">
            {exam.stats.map((s, i) => (
              <Reveal key={s.label} delay={i * 60}>
                <HeroStat value={s.value} label={s.label} />
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Sınavın amacı + kimler için */}
      <section className="bg-background-50 px-4 md:px-6 lg:px-10 py-14 md:py-20">
        <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-10">
          <div className="lg:col-span-7">
            <Reveal>
              <span className="inline-flex items-center gap-2.5 text-xs font-semibold uppercase tracking-[0.14em] text-foreground-500">
                <span className={`w-5 h-px ${tone.dot}`} aria-hidden="true"></span>
                Neden bu sınav?
              </span>
              <h2 className="mt-4 text-2xl md:text-3xl font-bold text-foreground-950 tracking-tight">
                Sınavın amacı
              </h2>
              <p className="mt-4 text-base md:text-lg text-foreground-700 leading-relaxed">
                {exam.purpose}
              </p>
            </Reveal>
          </div>
          <div className="lg:col-span-5">
            <Reveal delay={100}>
              <div className={`h-full rounded-lg border ${tone.border} ${tone.soft} p-6`}>
                <h3 className={`font-heading font-semibold text-base ${tone.text}`}>
                  Kimler için?
                </h3>
                <ul className="mt-4 space-y-3">
                  {exam.audience.map((a) => (
                    <li key={a} className="flex items-start gap-3">
                      <span
                        className={`mt-0.5 w-6 h-6 flex items-center justify-center rounded-full ${tone.soft} ${tone.text} shrink-0`}
                      >
                        <i className="ri-check-line text-sm" aria-hidden="true"></i>
                      </span>
                      <span className="text-sm md:text-base text-foreground-800 leading-snug">{a}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Dersler ve soru dağılımı */}
      <section className="bg-background-100 px-4 md:px-6 lg:px-10 py-14 md:py-20">
        <div className="max-w-6xl mx-auto">
          <Reveal>
            <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 tracking-tight">
              Dersler ve soru dağılımı
            </h2>
            <p className="mt-3 text-base text-foreground-600 leading-relaxed max-w-2xl">
              Bu sınavda hangi derslerden, ne tür sorularla karşılaşacağını gör.
            </p>
          </Reveal>
          <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {exam.subjects.map((s, i) => (
              <Reveal key={s.name} delay={i * 50}>
                <div className="flex items-start gap-4 p-5 rounded-lg border border-background-200 bg-background-50 hover:border-background-300 transition-colors h-full">
                  <span
                    className={`inline-flex w-10 h-10 shrink-0 items-center justify-center rounded-md ${tone.soft} ${tone.text}`}
                  >
                    <i className={`${s.icon} text-lg`} aria-hidden="true"></i>
                  </span>
                  <div className="flex-1">
                    <div className="flex items-center justify-between gap-2">
                      <h3 className="font-heading font-semibold text-base text-foreground-950">
                        {s.name}
                      </h3>
                      {typeof s.questions === "number" && (
                        <span
                          className={`shrink-0 inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-bold ${tone.soft} ${tone.text}`}
                        >
                          {s.questions} soru
                        </span>
                      )}
                    </div>
                    <p className="mt-1.5 text-sm text-foreground-600 leading-relaxed">{s.detail}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Nasıl hazırlanırsın — yolculuk */}
      <section className="bg-background-50 px-4 md:px-6 lg:px-10 py-14 md:py-20">
        <div className="max-w-6xl mx-auto">
          <Reveal>
            <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 tracking-tight">
              Nasıl hazırlanırsın?
            </h2>
            <p className="mt-3 text-base text-foreground-600 leading-relaxed max-w-2xl">
              Karmaşık değil, net bir akış: her adım seni bir sonrakine hazırlar.
            </p>
          </Reveal>
          <div
            className={`mt-8 grid grid-cols-1 sm:grid-cols-2 ${
              exam.journey.length >= 5 ? "lg:grid-cols-5" : "lg:grid-cols-4"
            } gap-4`}
          >
            {exam.journey.map((step, i) => (
              <Reveal key={step.title} delay={i * 60}>
                <div className="relative h-full p-5 rounded-lg border border-background-200 bg-background-50">
                  <span
                    className={`inline-flex w-8 h-8 items-center justify-center rounded-full text-sm font-bold text-background-50 ${tone.dot}`}
                  >
                    {i + 1}
                  </span>
                  <h3 className="mt-3 font-heading font-semibold text-base text-foreground-950">
                    {step.title}
                  </h3>
                  <p className="mt-1.5 text-sm text-foreground-600 leading-relaxed">{step.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Strateji ipucu + örnek rota */}
      <section className="bg-background-100 px-4 md:px-6 lg:px-10 py-14 md:py-20">
        <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-8">
          <Reveal>
            <div className={`h-full rounded-lg border ${tone.border} ${tone.soft} p-6 md:p-8`}>
              <span
                className={`inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide ${tone.text}`}
              >
                <i className="ri-lightbulb-line text-sm" aria-hidden="true"></i>
                Strateji ipucu
              </span>
              <h2 className="mt-3 font-heading font-semibold text-lg md:text-xl text-foreground-950">
                {exam.shortName} için öneriler
              </h2>
              <p className="mt-4 text-sm md:text-base text-foreground-800 leading-relaxed">
                {exam.strategy}
              </p>
            </div>
          </Reveal>

          <Reveal delay={100}>
            <div className="h-full rounded-lg border border-background-200 bg-background-50 p-6 md:p-8">
              <span className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-foreground-500">
                <i className="ri-eye-line text-sm" aria-hidden="true"></i>
                Örnek görünüm
              </span>
              <h2 className="mt-3 font-heading font-semibold text-lg md:text-xl text-foreground-950">
                Bugünkü rota örneği
              </h2>
              <p className="mt-4 text-sm md:text-base text-foreground-700 leading-relaxed">
                {exam.routeExample}
              </p>
              <Link
                to="/kayit"
                className="mt-6 inline-flex items-center justify-center gap-2 rounded-md bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-semibold px-6 py-3 whitespace-nowrap transition-colors"
              >
                Bu rota ile başla
                <i className="ri-arrow-right-line" aria-hidden="true"></i>
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      {/* İlgili sınavlar */}
      <section className="bg-background-50 px-4 md:px-6 lg:px-10 py-14 md:py-20">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 tracking-tight">
            Diğer sınavlar
          </h2>
          <div className="mt-6">
            <RelatedLinks items={related} />
          </div>
        </div>
      </section>

      <SiteCTA
        title={`${exam.shortName} hazırlığına bugün başla.`}
        text="Seviyeni seç, rotanı kur; her gün ne çalışacağını bil."
      />
    </PageShell>
  );
}